import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, RotateCcw, AlertTriangle, X } from 'lucide-react';
import ApiService from './ApiService';

const RecycleBinPage = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [purgeTarget, setPurgeTarget] = useState(null); // patient pending confirm
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.getDeletedPatients();
      setPatients(data.patients || []);
      setError('');
    } catch (err) {
      setError('Could not load recycle bin: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (patient) => {
    try {
      setWorking(true);
      await ApiService.restorePatient(patient.patientId);
      setMessage(`${patient.name} restored successfully.`);
      await load();
    } catch (err) {
      setError('Restore failed: ' + err.message);
    } finally {
      setWorking(false);
    }
  };

  const handlePurge = async () => {
    if (!purgeTarget) return;
    try {
      setWorking(true);
      await ApiService.purgePatient(purgeTarget.patientId);
      setMessage(`${purgeTarget.name} permanently deleted.`);
      setPurgeTarget(null);
      await load();
    } catch (err) {
      setError('Permanent delete failed: ' + err.message);
      setPurgeTarget(null);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-porcelain py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
            <Trash2 className="text-copper-500" size={22} /> Recycle Bin
          </h1>
          <p className="text-sm text-ink-soft mt-1">
            Deleted patients are kept here with all their records intact. Restore brings everything back;
            permanent deletion removes the patient and all associated data forever.
          </p>
        </div>

        {message && (
          <div className="mb-4 p-3 rounded-lg bg-spruce-50 border border-spruce-200 text-spruce-800 text-sm flex justify-between items-center">
            {message}
            <button onClick={() => setMessage('')}><X size={14} /></button>
          </div>
        )}
        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-danger text-danger text-sm">{error}</div>}

        <div className="bg-white border border-line rounded-xl overflow-hidden">
          {loading ? (
            <div className="px-4 py-12 text-center text-gray-500">Loading...</div>
          ) : patients.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              <Trash2 className="mx-auto mb-3 opacity-30" size={30} />
              The recycle bin is empty.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-mist text-left">
                  <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">Patient</th>
                  <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">Deleted</th>
                  <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">By</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {patients.map(p => (
                  <tr key={p.patientId} className="border-t border-line">
                    <td className="px-4 py-3">
                      <div className="font-medium text-ink">{p.name}</div>
                      <div className="text-xs text-ink-soft">{p.email}</div>
                      {p.mergedInto && (
                        <span className="inline-block mt-1 text-[10.5px] font-data border border-copper-500 text-copper-600 rounded-full px-2 py-0.5">
                          merged into another record
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-data text-xs text-ink-soft whitespace-nowrap">
                      {p.deletedAt ? new Date(p.deletedAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{p.deletedBy || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          disabled={working}
                          onClick={() => handleRestore(p)}
                          className="flex items-center gap-1.5 border border-spruce-500 text-spruce-700 hover:bg-spruce-50 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                        >
                          <RotateCcw size={13} /> Restore
                        </button>
                        <button
                          disabled={working}
                          onClick={() => setPurgeTarget(p)}
                          className="flex items-center gap-1.5 border border-danger text-danger hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                        >
                          <Trash2 size={13} /> Delete forever
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Purge confirmation */}
      {purgeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-line max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="text-danger" size={22} />
              <h2 className="text-lg font-semibold text-ink">Permanently delete {purgeTarget.name}?</h2>
            </div>
            <p className="text-sm text-ink-soft mb-5">
              This removes the patient and <strong>every associated record</strong> — medications, protocols,
              notes, services, and documents. A snapshot is kept in the audit log, but this cannot be undone
              from the portal.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setPurgeTarget(null)} className="border border-line px-4 py-2 rounded-lg text-sm font-medium hover:bg-mist">
                Cancel
              </button>
              <button onClick={handlePurge} disabled={working} className="bg-danger text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {working ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecycleBinPage;
